import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const useScrollReveal = (activeClass = 'active', selector = '.reveal-on-scroll') => {
  const location = useLocation();

  useEffect(() => {
    // Small delay so React finishes rendering the new route's elements before we query them
    const timer = setTimeout(() => {
      const observerOptions = {
        root: null,
        rootMargin: '0px 0px 0px 0px', // No negative bottom margin — reveals elements already in viewport on load/refresh
        threshold: 0.01                 // Fire as soon as 1% is visible
      };

      const handleIntersect = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(activeClass);
            // Unobserve once revealed to keep layout performant
            observer.unobserve(entry.target);
          }
        });
      };

      const observer = new IntersectionObserver(handleIntersect, observerOptions);
      const elements = document.querySelectorAll(selector);

      elements.forEach((el) => observer.observe(el));

      // Cleanup
      return () => {
        elements.forEach((el) => observer.unobserve(el));
        observer.disconnect();
      };
    }, 50); // 50ms delay to let React render the new page's elements

    return () => clearTimeout(timer);
  }, [activeClass, selector, location.pathname]); // Re-run on every route change
};

export default useScrollReveal;
